import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { fetchPostBySlug, fetchPosts } from "@/content/blog";
import Seo, { articleLd, breadcrumbLd, getSiteUrl } from "@/components/Seo";
import { ArrowLeft, ArrowRight, Clock, CalendarDays } from "lucide-react";

const SITE_URL = getSiteUrl();

function formatDate(iso, lang) {
  try {
    return new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function BlogPostPage({ forceLang }) {
  const { slug } = useParams();
  const { lang, setLang, t } = useLang();
  useEffect(() => {
    if (forceLang && forceLang !== lang) setLang(forceLang);
  }, [forceLang, lang, setLang]);
  const effectiveLang = forceLang || lang;
  const basePath = effectiveLang === "es" ? "/blog" : "/journal";

  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([fetchPostBySlug(effectiveLang, slug), fetchPosts(effectiveLang)]).then(
      ([p, all]) => {
        if (!alive) return;
        setPost(p);
        setRelated(
          (Array.isArray(all) ? all : []).filter((x) => x.slug !== slug).slice(0, 2)
        );
        setLoading(false);
      }
    );
    return () => {
      alive = false;
    };
  }, [effectiveLang, slug]);

  if (loading) {
    return (
      <main className="min-h-screen bg-cream-100">
        <ShopNav />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center text-olive-800/60">…</div>
        <Footer />
      </main>
    );
  }

  if (!post) {
    return (
      <main className="min-h-screen bg-cream-100">
        <ShopNav />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <p className="text-olive-800 font-serif text-2xl">{t("common.error_generic")}</p>
          <Link
            to={basePath}
            className="mt-6 inline-flex items-center gap-2 underline text-olive-900"
          >
            <ArrowLeft size={15} strokeWidth={1.3} />
            {t("blog.back_to_blog")}
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  const canonical = `${SITE_URL}${basePath}/${post.slug}`;
  const alternates = post.counterpart_slug
    ? {
        es: effectiveLang === "es" ? canonical : `${SITE_URL}/blog/${post.counterpart_slug}`,
        en: effectiveLang === "en" ? canonical : `${SITE_URL}/journal/${post.counterpart_slug}`,
        "x-default":
          effectiveLang === "es" ? canonical : `${SITE_URL}/blog/${post.counterpart_slug}`,
      }
    : undefined;

  return (
    <main data-testid="blog-post" className="min-h-screen bg-cream-100 text-charcoal">
      <Seo
        title={`${post.title} | Cortijo Santa María`}
        description={post.excerpt}
        canonical={canonical}
        alternates={alternates}
        ogImage={post.hero_image}
        lang={effectiveLang}
        ogType="article"
        jsonLd={[
          articleLd(post, canonical, effectiveLang),
          breadcrumbLd([
            { name: t("nav_ecom.home"), url: `${SITE_URL}/` },
            { name: t("blog.title"), url: `${SITE_URL}${basePath}` },
            { name: post.title, url: canonical },
          ]),
        ]}
      />
      <ShopNav />

      <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-16 sm:pt-20 pb-20">
        <Link
          to={basePath}
          data-testid="blog-back"
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-olive-800/70 hover:text-olive-900 mb-10"
        >
          <ArrowLeft size={13} strokeWidth={1.3} />
          {t("blog.back_to_blog")}
        </Link>

        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.26em] text-olive-800/60 mb-6">
          <span>{post.category}</span>
          {post.read_time ? (
            <>
              <span aria-hidden="true">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock size={11} strokeWidth={1.3} />
                {post.read_time} {t("blog.read_time_suffix")}
              </span>
            </>
          ) : null}
          <span aria-hidden="true">·</span>
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={11} strokeWidth={1.3} />
            {formatDate(post.date, effectiveLang)}
          </span>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-olive-900">
          {post.title}
        </h1>

        <p className="mt-6 text-olive-800/85 text-base sm:text-lg leading-relaxed font-light italic border-l-2 border-gold-deep pl-4">
          {post.excerpt}
        </p>

        {post.hero_image ? (
          <div className="mt-10 aspect-[16/9] bg-cream-200 overflow-hidden border border-cream-400">
            <img
              src={post.hero_image}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </div>
        ) : null}

        <div className="mt-10 space-y-6 text-olive-900 font-serif text-[17px] sm:text-lg leading-[1.8]">
          {(post.body || []).map((para, i) => (
            <p
              key={`${post.slug}-p-${i}`}
              className={i === 0 ? "first-letter:font-serif first-letter:text-5xl first-letter:font-medium first-letter:mr-2 first-letter:float-left first-letter:leading-[0.9] first-letter:text-gold-deep" : ""}
            >
              {para}
            </p>
          ))}
        </div>

        {/* Inline CTA */}
        <aside className="mt-16 p-8 lg:p-10 bg-olive-900 text-cream-100 flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex-1">
            <p className="text-[11px] uppercase tracking-[0.28em] text-gold mb-3">
              {t("blog.overline")}
            </p>
            <h3 className="font-serif text-xl lg:text-2xl leading-snug">
              {t("blog.cta_title")}
            </h3>
            <p className="mt-3 text-sm text-cream-100/75 font-light leading-relaxed">
              {t("blog.cta_body")}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/tienda"
              data-testid="blog-cta-shop"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-gold-deep text-olive-900 text-[11px] uppercase tracking-[0.26em] hover:bg-gold transition-colors"
            >
              {t("blog.cta_shop")}
              <ArrowRight size={13} strokeWidth={1.3} />
            </Link>
            <Link
              to="/producto/botella-500ml"
              className="inline-flex items-center gap-2 px-6 py-3.5 border border-cream-100/50 text-cream-100 text-[11px] uppercase tracking-[0.26em] hover:border-cream-100"
            >
              {t("blog.cta_product")}
            </Link>
          </div>
        </aside>

        {related.length > 0 && (
          <div className="mt-20 pt-10 border-t border-cream-400">
            <h4 className="font-serif text-xl text-olive-900 mb-6">
              {t("blog.related_title")}
            </h4>
            <div className="grid sm:grid-cols-2 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`${basePath}/${r.slug}`}
                  data-testid={`blog-related-${r.slug}`}
                  className="group block p-5 border border-cream-400 hover:border-olive-700 bg-cream-50 transition-colors"
                >
                  <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-olive-800/60 mb-2">
                    {r.category}
                  </div>
                  <div className="font-serif text-lg text-olive-900 group-hover:text-gold-deep transition-colors leading-snug">
                    {r.title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer />
    </main>
  );
}
